import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as rfidActions from './rfid.actions';
import * as rfidSelectors from './rfid.selectors';
import * as _operatorsActions from '../../../store/operators/operators.actions';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import { RfidService } from "../services/rfid.service";
import { catchError, filter, map, mergeMap, of, switchMap } from "rxjs";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { RfidGroupRequestUtil } from "../utils/rfid-groups-request.utils";
import { MessageTranslateService } from "../../../shared/services/message.service";
import { ErrorModel } from "../../../core/cms-api/models";

/** Backend rejected the token's user because they have an unpaid invoice. */
export const USER_HAS_UNPAID_INVOICE_CODE = 116;

/** Non-2xx responses may still carry the `Result` envelope in the body. */
function toErrorModel(err: any, fallbackMessage: string): ErrorModel {
  return err?.error?.error ?? { errorMessage: err?.message ?? fallbackMessage };
}

export class RfidEffects {
  actions$: Actions = inject(Actions);
  rfidService = inject(RfidService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  assignUserToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.assignUserTokenToCustomer),
      switchMap((request) => {
        return this.rfidService
          .assignTokenToUser(request.userTokenId, request.userId)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return rfidActions.updateUserTokenMetadataSuccess({
                  result: res.value!,
                });
              } else {
                return rfidActions.updateUserTokenMetadataFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                rfidActions.updateUserTokenMetadataFailure({
                  error: toErrorModel(err, 'Assigning user to token failed'),
                }),
              ),
            ),
          );
      }),
    ),
  );

  updateRfidMetadatMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          rfidActions.updateUserTokenMetadataSuccess,
          rfidActions.updateUserTokenMetadataFailure,
        ),
        map((action) => {
          if (action.type == rfidActions.updateUserTokenMetadataSuccess.type) {
            this.messageService.successTranslate('rfidGroups.overview.insert-rfid.update-success-message');
          }
          if (action.type == rfidActions.updateUserTokenMetadataFailure.type) {
            this.showUserTokenSaveError(action.error, 'rfidGroups.overview.insert-rfid.update-failure-message');
          }
        }),
      ),
    { dispatch: false },
  );

  updateRfidMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.updateUserTokenMetadata),
      switchMap((request) => {
        return this.rfidService
          .updateUserTokenMetadata(request.userTokenId, request.updateUserToken)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return rfidActions.updateUserTokenMetadataSuccess({
                  result: res.value!,
                });
              } else {
                return rfidActions.updateUserTokenMetadataFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                rfidActions.updateUserTokenMetadataFailure({
                  error: toErrorModel(err, 'Updating user token failed'),
                }),
              ),
            ),
          );
      }),
    ),
  );

  changeRfidGroupStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.changeRfidGroupStatus),
      switchMap((request) => {
        return this.rfidService
          .updateRfidGroupStatus(request.rfidGroupId, request.status)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return rfidActions.changeRfidGroupStatusSuccess({
                  rfidGroupId: request.rfidGroupId,
                  status: request.status,
                });
              }

              return rfidActions.changeRfidGroupStatusFailure({
                error: res.error!,
              });
            }),
            catchError((err) =>
              of(
                rfidActions.changeRfidGroupStatusFailure({
                  error: err.message || 'Loading rfid groups failed',
                }),
              ),
            ),
          );
      }),
    ),
  );

  changedRfidGroupsPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.changeRfidGroupPagination),
      map((_) => rfidActions.changeRfidGroupPaginationSuccessful()),
    ),
  );

  changeUserTokenPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.changeUserTokenPagination),
      map((_) => rfidActions.changeUserTokenPaginationSuccessful()),
    ),
  );

  updateRfidGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.updateRfidGroup),
      switchMap((requestData) =>
        this.rfidService
          .updateRfidGroup(
            requestData.rfidGrupId,
            requestData.updateRfidGroupRequest,
          )
          .pipe(
            mergeMap((res) => {
              if (res.isSuccess) {
                const resValue = res.value!;
                return of(
                  rfidActions.updateRfidGroupSuccess({ result: resValue }),
                  rfidActions.loadRfidGroupOverview({
                    rfidGroupId: requestData.rfidGrupId,
                  }),
                );
              }
              return of(
                rfidActions.updateRfidGroupFailure({ error: res.error! }),
              );
            }),
          ),
      ),
    ),
  );

  insertNewRfidGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.insertNewRfidGroup),
      switchMap((requestData) =>
        this.rfidService
          .createRfidGroup(requestData.createRfidGroupRequest)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return rfidActions.insertNewRfidGroupSuccess({
                  result: res.value!,
                });
              }
              return rfidActions.insertNewRfidGroupFailure({
                error: res.error!,
              });
            }),
          ),
      ),
    ),
  );

  loadRfidGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        rfidActions.loadRfidGroupList,
        rfidActions.changeRfidGroupPaginationSuccessful,
        rfidActions.insertNewRfidGroupSuccess,
        // operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(rfidSelectors.selectRfidGroupsCurrentFilter),
        this.store.select(rfidSelectors.selectRfidGroupsPagination),
      ]),
      switchMap(([_action, selectedOperator, currentFilter, pagination]) =>
        this.rfidService
          .searchRfidGroups(
            RfidGroupRequestUtil.createRfidGroupListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter,
            ),
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return rfidActions.loadRfidGroupListSuccess({
                  rfidGroupsResponse: res.value!,
                });
              } else {
                return rfidActions.loadRfidGroupListFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                rfidActions.loadRfidGroupListFailure({
                  error: err.message || 'Loading rfid groups failed',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadRfidGroupOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.loadRfidGroupOverview),
      switchMap((requestData) =>
        this.rfidService.getRfidGroupOverview(requestData.rfidGroupId).pipe(
          map((res) => {
            if (res.isSuccess) {
              return rfidActions.loadRfidGroupOverviewSuccess({
                result: res.value!,
              });
            } else {
              return rfidActions.loadRfidGroupOverviewFailure({
                error: res.error!,
              });
            }
          }),
          catchError((err) =>
            of(
              rfidActions.loadRfidGroupOverviewFailure({
                error: err.message || 'Loading rfid group overview failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadUserTokensRfidList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        rfidActions.loadRfidGroupOverviewSuccess,
        rfidActions.changeUserTokenPaginationSuccessful,
        rfidActions.loadUserTokensRfidList,
        rfidActions.updateUserTokenMetadataSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(rfidSelectors.selectRfidGroupOverview),
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(rfidSelectors.selectUserTokensPagination),
        this.store.select(rfidSelectors.selectUserTokensCurrentFilter),
      ]),
      filter(([_, overviewData]) => !!overviewData?.id),
      switchMap(
        ([
          _requestData,
          overviewData,
          operator,
          userTokensPagination,
          currentFilter,
        ]) =>
          this.rfidService
            .searchRfids({
              operatorId: operator!.id!,
              rfidGroupId: overviewData!.id,
              isActive: currentFilter?.isActive,
              pageIndex: userTokensPagination!.pageIndex!,
              pageSize: userTokensPagination!.pageSize!,
            })
            .pipe(
              mergeMap((res) => {
                if (res.isSuccess)
                  return of(
                    rfidActions.loadUserTokensRfidListSuccess({
                      userTokens: res.value!,
                    }),
                  );
                return of(
                  rfidActions.loadUserTokensRfidListFailure({
                    error: res.error!,
                  }),
                );
              }),
            ),
      ),
    ),
  );

  insertNewUserToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.insertNewUserToken),
      switchMap((requestData) =>
        this.rfidService
          .createUserTokenRfid(requestData.createUserTokenRfid)
          .pipe(
            mergeMap((res) => {
              if (res.isSuccess)
                return of(
                  rfidActions.insertNewUserTokenSuccess({ result: res.value! }),
                  rfidActions.loadRfidGroupOverview({
                    rfidGroupId: requestData.createUserTokenRfid!.rfidGroupId,
                  }),
                );

              return of(
                rfidActions.insertNewUserTokenFailure({ error: res.error! }),
              );
            }),
            catchError((err) =>
              of(
                rfidActions.insertNewUserTokenFailure({
                  error: toErrorModel(err, 'Creating user token failed'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  blockUserToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.blockUserToken),
      concatLatestFrom(() => [
        this.store.select(rfidSelectors.selectRfidGroupOverview),
      ]),
      switchMap(([requestData, overviewData]) =>
        this.rfidService.blockUserToken(requestData.userTokenId).pipe(
          mergeMap((res) => {
            if (res.isSuccess) {
              return of(
                rfidActions.blockUserTokenSuccess({
                  userTokenId: requestData.userTokenId,
                }),
                rfidActions.loadRfidGroupOverview({
                  rfidGroupId: overviewData!.id,
                }),
              );
            }
            return of(rfidActions.blockUserTokenFailure({ error: res.error! }));
          }),
        ),
      ),
    ),
  );

  unblockUserToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.unblockUserToken),
      concatLatestFrom(() => [
        this.store.select(rfidSelectors.selectRfidGroupOverview),
      ]),
      switchMap(([requestData, overviewData]) =>
        this.rfidService.unlockUserToken(requestData.userTokenId).pipe(
          mergeMap((res) => {
            if (res.isSuccess) {
              return of(
                rfidActions.unblockUserTokenSuccess({
                  userTokenId: requestData.userTokenId,
                }),
                rfidActions.loadRfidGroupOverview({
                  rfidGroupId: overviewData!.id,
                }),
              );
            }
            return of(
              rfidActions.unblockUserTokenFailure({ error: res.error! }),
            );
          }),
        ),
      ),
    ),
  );

  deleteUserToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.deleteUserToken),
      concatLatestFrom(() => [
        this.store.select(rfidSelectors.selectRfidGroupOverview),
      ]),
      switchMap(([requestData, rfidGroupOverview]) =>
        this.rfidService.deleteUserTokenRfid(requestData.userTokenId).pipe(
          mergeMap((res) => {
            if (res.isSuccess) {
              return of(
                rfidActions.deleteUserTokenSuccess({
                  userTokenId: requestData.userTokenId,
                }),
                rfidActions.loadRfidGroupOverview({
                  rfidGroupId: rfidGroupOverview!.id,
                }),
              );
            }
            return of(
              rfidActions.deleteUserTokenFailure({ error: res.error! }),
            );
          }),
        ),
      ),
    ),
  );

  loadEmpUserTokensList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.loadEmpUserTokensList),
      switchMap((action) =>
        this.rfidService.getEmpUserTokensForUserList(action.userIds).pipe(
          map((res) => {
            if (res.isSuccess) {
              return rfidActions.loadEmpUserTokensListSuccess({
                userTokens: res.value!,
              });
            }
            return rfidActions.loadEmpUserTokensListFailure({
              error: res.error!,
            });
          }),
          catchError((err) =>
            of(
              rfidActions.loadEmpUserTokensListFailure({
                error: {
                  errorMessage: err.message ?? 'Loading emp user tokens failed',
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  showSuccessNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          rfidActions.insertNewRfidGroupSuccess,
          rfidActions.insertNewUserTokenSuccess,
          rfidActions.blockUserTokenSuccess,
          rfidActions.unblockUserTokenSuccess,
          rfidActions.deleteUserTokenSuccess,
          rfidActions.updateRfidGroupSuccess,
          rfidActions.changeRfidGroupStatusSuccess,
        ),
        map((action) => {
          if (action.type == rfidActions.insertNewRfidGroupSuccess.type) {
            this.messageService.successTranslate(
              'rfidGroups.insert.save-success',
            );
          } else if (
            action.type == rfidActions.insertNewUserTokenSuccess.type
          ) {
            this.messageService.successTranslate(
              'rfidGroups.overview.insert-rfid.save-success',
            );
          } else if (action.type == rfidActions.blockUserTokenSuccess.type) {
            this.messageService.successTranslate(
              'rfidGroups.overview.rfid-list.block-success',
            );
          } else if (action.type == rfidActions.unblockUserTokenSuccess.type) {
            this.messageService.successTranslate(
              'rfidGroups.overview.rfid-list.unblock-success',
            );
          } else if (action.type == rfidActions.deleteUserTokenSuccess.type) {
            this.messageService.successTranslate(
              'rfidGroups.overview.rfid-list.delete-success',
            );
          } else if (action.type == rfidActions.updateRfidGroupSuccess.type) {
            this.messageService.successTranslate(
              'rfidGroups.insert.save-success',
            );
          } else if (
            action.type == rfidActions.changeRfidGroupStatusSuccess.type
          ) {
            this.messageService.successTranslate(
              'rfidGroups.overview.status-change-success',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  showErrorNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          rfidActions.insertNewRfidGroupFailure,
          rfidActions.insertNewUserTokenFailure,
          rfidActions.blockUserTokenFailure,
          rfidActions.unblockUserTokenFailure,
          rfidActions.deleteUserTokenFailure,
          rfidActions.updateRfidGroupFailure,
          rfidActions.changeRfidGroupStatusFailure,
        ),
        map((action) => {
          if (action.type == rfidActions.insertNewRfidGroupFailure.type) {
            this.messageService.errorTranslate('rfidGroups.insert.save-error');
          } else if (
            action.type == rfidActions.insertNewUserTokenFailure.type
          ) {
            this.showUserTokenSaveError(
              action.error,
              'rfidGroups.overview.insert-rfid.save-error',
            );
          } else if (action.type == rfidActions.blockUserTokenFailure.type) {
            this.messageService.errorTranslate(
              'rfidGroups.overview.rfid-list.block-failed',
            );
          } else if (action.type == rfidActions.unblockUserTokenFailure.type) {
            this.messageService.errorTranslate(
              'rfidGroups.overview.rfid-list.unblock-failed',
            );
          } else if (action.type == rfidActions.deleteUserTokenFailure.type) {
            this.messageService.errorTranslate(
              'rfidGroups.overview.rfid-list.delete-failed',
            );
          } else if (action.type == rfidActions.updateRfidGroupFailure.type) {
            this.messageService.errorTranslate('rfidGroups.insert.save-error');
          } else if (
            action.type === rfidActions.changeRfidGroupStatusFailure.type
          ) {
            this.messageService.errorTranslate(
              'rfidGroups.overview.status-change-error',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  rfidGroupChnage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.changeRfidGroupFilter),
      map((_) => rfidActions.loadRfidGroupList()),
    ),
  );

  userTokenFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.changeUserTokenFilter),
      map((_) => rfidActions.loadUserTokensRfidList()),
    ),
  );

  loadInternalBillingUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rfidActions.loadInternalBillingUsers),
      concatLatestFrom(() =>
        this.store.select(operatorsSelectors.selectCurrentOperator),
      ),
      switchMap(([_action, operator]) => {
        if (!operator?.id) {
          return of(
            rfidActions.loadInternalBillingUsersFailure({
              error: { errorMessage: 'Operator not selected' },
            }),
          );
        }

        return this.rfidService.getInternalBillingUsers(operator.id).pipe(
          map((res) => {
            if (res.isSuccess) {
              return rfidActions.loadInternalBillingUsersSuccess({
                users: res.value ?? [],
              });
            }
            return rfidActions.loadInternalBillingUsersFailure({
              error: res.error!,
            });
          }),
          catchError((err) =>
            of(
              rfidActions.loadInternalBillingUsersFailure({
                error: toErrorModel(err, 'Loading internal billing users failed'),
              }),
            ),
          ),
        );
      }),
    ),
  );

  loadInternalBillingUsersFailureMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(rfidActions.loadInternalBillingUsersFailure),
        map(() => this.messageService.errorTranslate('errors.codes.946')),
      ),
    { dispatch: false },
  );

  /** Shows the unpaid-invoice message for code 116, otherwise the generic save error. */
  private showUserTokenSaveError(error: ErrorModel | null | undefined, fallbackKey: string): void {
    if (error?.errorCode === USER_HAS_UNPAID_INVOICE_CODE) {
      this.messageService.errorTranslate(`errors.codes.${USER_HAS_UNPAID_INVOICE_CODE}`);
    } else {
      this.messageService.errorTranslate(fallbackKey);
    }
  }
}